import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { LucideIcon } from "@/components/icons"

import { CheckCircle2, FileText, Loader2, Search, Telescope, Users } from "@/components/icons"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

type Phase = {
  value: "plan" | "search" | "synthesis"
  labelKey: string
  icon: LucideIcon
}

const PHASES: ReadonlyArray<Phase> = [
  { value: "plan", labelKey: "phase_plan", icon: Telescope },
  { value: "search", labelKey: "phase_search", icon: Search },
  { value: "synthesis", labelKey: "phase_synthesis", icon: FileText },
] as const

export function DemoDeepPhases() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-agents.helloai.deep.demo" })

  const planSteps = useMemo<string[]>(
    () => [
      t("plan_step_1", "Identify key markets and competitors"),
      t("plan_step_2", "Collect growth data for the past 3 years"),
      t("plan_step_3", "Analyze regulatory differences"),
      t("plan_step_4", "Compile recommendations"),
    ],
    [t]
  )

  const searchActivities = useMemo<
    Array<{ role: string; tool: string; status: "done" | "running" }>
  >(
    () => [
      {
        role: t("subagent_scout", "Scout"),
        tool: t("tool_web_search", "Web search"),
        status: "done",
      },
      {
        role: t("subagent_worker_1", "Worker 1"),
        tool: t("tool_library", "Company library"),
        status: "done",
      },
      {
        role: t("subagent_worker_2", "Worker 2"),
        tool: t("tool_files", "Uploaded files"),
        status: "running",
      },
    ],
    [t]
  )

  const sources = useMemo<string[]>(
    () => [
      t("source_1", "Industry report — Q4 2025"),
      t("source_2", "Internal market analysis (2024-11-12)"),
      t("source_3", "Competitor X annual report"),
    ],
    [t]
  )

  return (
    <div className="w-full">
      <Tabs className="w-full" defaultValue="plan">
        <TabsList className="w-full justify-start">
          {PHASES.map(({ value, labelKey, icon: Icon }) => (
            <TabsTrigger key={value} value={value}>
              <Icon className="size-4" />
              <span>{t(labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="plan">
          <div className="bg-background rounded-lg border p-4">
            <p className="text-muted-foreground mb-3 text-xs">
              {t("plan_caption", "Deep drafts a plan you can approve or edit.")}
            </p>
            <ol className="space-y-2">
              {planSteps.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="bg-primary/10 text-primary flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="search">
          <div className="bg-background rounded-lg border p-4">
            <p className="text-muted-foreground mb-3 flex items-center gap-1.5 text-xs">
              <Users className="size-3.5" />
              {t("search_caption", "Sub-agents search the chosen sources in parallel.")}
            </p>
            <ul className="space-y-2">
              {searchActivities.map((a, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {a.status === "running" ? (
                    <Loader2 className="text-primary size-4 shrink-0 animate-spin" />
                  ) : (
                    <CheckCircle2 className="text-success size-4 shrink-0" />
                  )}
                  <span className="text-muted-foreground font-mono text-xs">{a.role}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{a.tool}</span>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="synthesis">
          <div className="bg-background rounded-lg border p-4">
            <p className="text-muted-foreground mb-3 text-xs">
              {t(
                "synthesis_caption",
                "The output is compiled into a structured deliverable with sources."
              )}
            </p>
            <div className="mb-3 rounded-md border p-3 text-sm">
              <p className="font-semibold">
                {t("deliverable_title", "Market analysis — Nordics 2026")}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                {t("deliverable_subtitle", "12-page PDF · 14 sources · high confidence")}
              </p>
            </div>
            <p className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
              {t("sources_label", "Sources")}
            </p>
            <ul className="space-y-1.5">
              {sources.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="bg-muted text-muted-foreground mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] font-semibold">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
